<template>
  <div v-if="currentCrm">
    <nav class="woocommerce-breadcrumb" itemprop="breadcrumb">
      <a href="/dashboard">Dashboard</a> / <a href="/integrations/catalogue">Integrations</a> /
      <span> {{ currentCrm.commercialName }} CRM Details </span>
    </nav>

    <div class="container-fluid integration-details-page">
      <div class="integration-details-page__header">
        <img
          :src="`/wp-content/themes/snappy-theme/assets/images/crms/${crm}.svg`"
          :alt="crm"
          class="integration-details-page__logo"
        />
        <div class="integration-details-page__title">{{ currentCrm.commercialName }} CRM</div>
        <div class="integration-details-page__subtitle">{{ details.moto }}</div>
        <div class="integration-details-page__badge">
          <snappy-badge :background="isActive ? 'green' : 'grey'">{{
            isActive ? 'Connected' : currentCrm.status
          }}</snappy-badge>
        </div>
        <div class="integration-details-page__actions">
          <a href="https://intercom.help/snappy-kraken/en/" target="_blank" class="btn btn-lg btn-primary"
            >Learn More</a
          >
          <template v-if="isActive">
            <a :href="`/account/${crm}/`" class="btn btn-lg btn-primary">Settings</a>
            <a href="/integrations/catalogue/disconnect" class="btn btn-lg btn-primary">Disconnect</a>
          </template>
          <button @click="connect" class="btn btn-lg btn-primary" :class="{disabled: connectionDisabled}">
            Connect
          </button>
        </div>
      </div>
      <div class="integration-details-page__body row d-flex">
        <div class="integration-details-page__main col-sm-12 col-md-8">
          <div class="integration-details-page__tabs-wrapper">
            <ul class="nav nav-tabs integration-details-page__tabs" role="tablist">
              <li class="active integration-details-page__tab">
                <a
                  href="#info"
                  class="integration-details-page__tab-link"
                  aria-controls="info"
                  role="tab"
                  data-toggle="tab"
                  >Overall Info</a
                >
              </li>
            </ul>

            <div class="tab-content integration-details-page__tab-content">
              <div role="tabpanel" class="tab-pane integration-details-page__tab-panel active row" id="info">
                <div class="col-md-5">
                  <h2 class="integration-details-page__heading">
                    {{ details.overallInfo.title }}
                  </h2>
                </div>
                <div class="col-md-7">
                  <p v-for="(paragraph, index) in details.overallInfo.paragraphs" :key="index">
                    {{ paragraph }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="integration-details-page__sidebar col-xs-12 col-md-4">
          <h3 class="integration-details-page-about__title">About {{ currentCrm.commercialName }}</h3>
          <div>{{ details.about.desc }}</div>
          <a
            :href="details.about.link"
            class="integration-details-page-about__link integration-details-page-about__link--disabled"
          >
            Find out more about {{ currentCrm.commercialName }}.
          </a>
          <div class="integration-details-page-about__items">
            <div class="integration-details-page-about__item" v-for="item in details.about.items" :key="item.title">
              <h4 class="integration-details-page-about__item-title">{{ item.title }}</h4>
              <ul class="integration-details-page-about__item-list">
                <li
                  v-for="(listItem, index) in item.list"
                  :key="index"
                  class="integration-details-page-about__item-tip"
                >
                  {{ listItem }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <integrations-footer></integrations-footer>
    </div>
  </div>
</template>

<script>
import SnappyBadge from '../components/SnappyBadge.vue';
import SnappyDropdown from '../components/SnappyDropdown.vue';
import IntegrationsFooter from '../components/integrations/IntegrationsFooter.vue';
import {crmsData} from '../components/integrations/crmsData.js';
import {getConnectLink} from '../components/integrations/getConnectLink';

export default {
  name: 'integration-details-page',
  components: {
    SnappyBadge,
    SnappyDropdown,
    IntegrationsFooter,
  },
  data() {
    return {
      actions: [
        {text: 'Sync Management', href: '/sync/management'},
        {text: 'Import History', href: '/inventory/contacts/imports'},
        {text: 'Disconnect', href: '/integrations/catalogue/disconnect'},
      ],
      cards: [],
    };
  },
  props: {
    crm: {
      type: String,
    },
    activeCrm: {
      type: String,
    },
    platformTitle: {
      type: String,
      required: true,
    },
  },
  computed: {
    activeCrmData() {
      return JSON.parse(this.activeCrm);
    },
    currentCrm() {
      return this.cards?.find(item => item.name === this.crm);
    },
    details() {
      const {details} = this.currentCrm;
      return details;
    },
    isActive() {
      return this.activeCrmData.crm_name === this.crm;
    },
    connectionDisabled() {
      return this.currentCrm.status === 'disabled' || (this.activeCrmData?.length && !isActive);
    },
  },
  created() {
    this.cards = crmsData(this.platformTitle);
  },
  methods: {
    connect() {
      getConnectLink(this.crm);
    },
  },
};
</script>

<style>
@import '../../sass/templates/integrations/_integrations.scss';
</style>

<style lang="scss" scoped>
@import '../../sass/web/_variables.scss';

.integration-details-page {
  &__header {
    background: $white-color;
    min-height: 144px;
    padding: 16px;
    box-shadow: inset 0px -1px 0px $platinum-color;
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-areas:
      'logo badge'
      'title title'
      'subtitle subtitle'
      'actions actions';
    @media (min-width: $breakpoint-md) {
      padding: 24px;
      grid-template-columns: 110px 1fr max-content;
      grid-template-rows: min-content min-content;
      grid-template-areas:
        'logo title badge'
        'logo subtitle actions';
    }
  }

  &__title,
  &__subtitle {
    text-align: left;
    font-weight: bold;
  }
  &__title {
    font-size: 32px;
    line-height: 52px;
    color: $mine-shaft-color;
    grid-area: title;
    @media (min-width: $breakpoint-md) {
      font-size: 48px;
    }
  }
  &__subtitle {
    font-size: 16px;
    align-self: flex-end;
    grid-area: subtitle;
    margin-bottom: 20px;
    @media (min-width: $breakpoint-md) {
      font-size: 20px;
      margin-bottom: 0;
    }
  }
  &__logo {
    width: 72px;
    height: 72px;
    grid-area: logo;
    @media (min-width: $breakpoint-md) {
      width: 96px;
      height: 96px;
    }
  }
  &__badge {
    text-align: right;
    grid-area: badge;
  }
  &__actions {
    display: inline-flex;
    align-items: flex-end;
    gap: 5px;
    grid-area: actions;
    flex-wrap: wrap;
  }

  &__main {
    text-align: left;
  }
  &__body {
    flex-wrap: wrap;
  }
  &__tabs-wrapper {
    padding: 16px;
    @media (min-width: $breakpoint-md) {
      padding: 16px 24px;
    }
  }

  &__tabs {
    display: inline-flex;
    margin: 0;
  }

  &__tab {
    background: #e5e5e5;
    border: none;
    margin: 0;
    position: relative;
    top: 1px;
    border-bottom: 1px solid #bbbbbb;
    &[disabled] {
      pointer-events: none;
    }
    &-link {
      margin: 0;
      border: none;
      background: transparent;
      font-size: 14px;
      color: #969696;
      &:hover {
        background: transparent;
      }
    }
    &-content {
      padding: 0;
    }
  }

  &__heading {
    padding: 16px 0 10px;
    position: relative;
    margin-top: 0;
    &::after {
      content: '';
      display: inline-flex;
      position: absolute;
      top: 100%;
      left: 0;
      height: 8px;
      width: 107px;
      background: $half-baked-color;
    }
  }

  &__tab.active {
    border-bottom: 2px solid $half-baked-color;
    background: transparent;
  }

  &__tab.active &__tab-link {
    background: transparent;
    border: none;
    color: $mine-shaft-color;
  }

  &__sidebar {
    box-shadow: inset 1px 0px 0px $platinum-color;
    text-align: left;
    padding: 16px 16px 16px 31px;
    @media (min-width: $breakpoint-lg) {
      background: $white-color;
    }
    @media (min-width: $breakpoint-md) {
      padding: 24px 40px 24px;
    }
  }

  &-about {
    &__title {
      font-size: 24px;
      font-weight: 600;
    }
    &__link {
      color: $half-baked-color;
      text-decoration: underline;
      font-weight: bold;
      &--disabled {
        pointer-events: none;
        color: $dove-grey-color;
        text-decoration: none;
      }
    }
    &__items {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-template-rows: repeat(2, 1fr);
      margin-top: 20px;
      gap: 10px;
    }
    &__item {
      flex: 1;
      flex-shrink: 0;
      &-title {
        font-size: 16px;
      }
      &-list {
        margin: 0;
      }
      &-tip {
        list-style-position: inside;
      }
    }
  }
  &__footer {
    background: #e0ebeb;
    min-height: 200px;
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
    padding: 24px;
    &-links {
      display: block;
      margin: 0 auto;
    }
    &-link {
      display: flex;
      gap: 8px;
      margin-bottom: 10px;
      justify-content: center;
      color: #1da5a4;
      font-weight: bold;

      @media (min-width: $breakpoint-sm) {
        display: inline-flex;
        &:not(:last-child) {
          border-right: 1px solid#BDC6C6;
          padding-right: 25px;
          margin-right: 25px;
        }
      }
    }
  }
}
</style>
